import { debug, i18n } from "aaa";
import * as _ from "lodash";
import { action, computed, observable } from "mobx";
import { APIClientStatusCodeError } from "network/NetworkStapler";
import { ErrorCode, IApiError } from "types/error";
import { GenericAPIClient, NetbankingAPIClient } from "network/APIClient";
import SigningAPI from "network/SigningAPI";
import { IFlickerParams, IPhoneOption, ISignData, ISigningInfo, SignMode, IPollingInfo } from "types/signing";
import { safeWindowOpen } from "util/helpers";
import externalInterfaceStore from "stores/ExternalInterfaceStore";

// States of the SigningStore
export type SigningState =
    "INIT" |
    "SIGNING_STARTED" |
    "CHOOSE_PHONE" |
    "WAIT_FOR_AUTHORIZATION" |
    "ERROR" |
    "DONE";

let currentSigningStore: any;

/**
 * Store for handling signing procedure.
 *
 * Signing flow is 
 *      getSign -> postSign -> putSign for TAC and cardTAN
 *      getSign -> postSign -> pollSign -> putSign for sID
 * 
 * - getSign retrieves sign ID, authorizationType and array of phones
 * - postSign tells backend which phone is used
 * - pollSign polls the signing state for sID authorization
 * - putSign sends the TAC 
 */
export class SigningStore {
    signingInfo?: ISigningInfo;

    // gets identified by startSigning
    @observable signingMethod?: SignMode;

    @observable private _signingState: SigningState;

    get signingState() {
        return this._signingState;
    }

    set signingState(newSigningState: SigningState) {
        debug.log("### Signing state from " + this._signingState + " to " + newSigningState);
        this._signingState = newSigningState;
    }

    @observable signingErrorState: Array<ErrorCode> = [];

    @computed get hasError() {
        return this.signingErrorState && this.signingErrorState.length > 0;
    }

    // gets array from startSigning
    phoneOptions: Array<IPhoneOption> | undefined;

    @observable isLoading: boolean = false;

    @observable flickerParams?: IFlickerParams;

    pollingOnSuccess?: (data: any) => void;
    pollingOnError?: (error: any) => void;
    pollingData?: IPollingInfo;
    pollingTimer?: number;

    constructor() {
        this._signingState = "INIT";
        currentSigningStore = this;
        debug.log("### signing store constructor");
    }

    setPollingHandlers(onSuccess: (data: any) => void, onError?: (error: any) => void) {
        this.pollingOnSuccess = onSuccess;
        this.pollingOnError = onError;
    }

    @action startSigning = async (): Promise<ISignData | undefined> => {
        if (this.signingInfo) {
            this.signingState = "SIGNING_STARTED";

            const target = SigningAPI.getSign(this.signingInfo);
            this.isLoading = true;
            this.signingErrorState = [];

            debug.log("### start signing", target.url);

            try {
                const signData = await NetbankingAPIClient.requestType(target);
                this.isLoading = false;

                this.signingMethod = signData.authorizationType;
                this.phoneOptions = signData.phones;

                // Save flicker parameters for cardTAN signing
                if (signData.challengeCode && signData.challengeImage) {
                    this.flickerParams = {
                        startCodeValue: signData.challengeCode,
                        flickerCode: atob(signData.challengeImage)
                    };
                }

                debug.log("### getSign returned", signData);

                switch (this.signingMethod) {
                    case "TAC":
                        if (this.phoneOptions) {
                            if (this.phoneOptions.length === 1) {
                                const phone = this.phoneOptions[0];
                                return this.postSign(phone);
                            } else {
                                this.signingState = "CHOOSE_PHONE";
                            }
                        } else {
                            // This should never happen
                            this.signingErrorState.push("UNKNOWN");
                        }
                        break;
                    case "SID":
                        return this.postSign();
                    case "CARDTAN":
                        return this.postSign();
                    case "GEORGE_TOKEN":
                        return this.postSign();
                    default:
                        this.signingErrorState.push("UNKNOWN");
                        throw new Error("unknown signing method");
                }

                return signData;
            } catch (e) {
                this.checkError(e);
                this.isLoading = false;
                throw e;
            }
        } else {
            throw new Error("sign info missing");
        }
    }

    /**
     * Second step in signing procedure
     * - In case of TAC tells backend which phone to use
     * - In case of SID returns the polling parameters
     * @param phone 
     */
    @action async postSign(phone?: IPhoneOption): Promise<ISignData | undefined> {
        if (this.signingInfo
            && this.signingMethod
        ) {
            if (phone) {
                debug.log("### postSign for phone", phone);
            }

            const target = SigningAPI.postSign(this.signingMethod, this.signingInfo, phone ? phone.id : undefined);
            this.isLoading = true;

            try {
                const signData = await NetbankingAPIClient.requestType(target);
                this.isLoading = false;

                debug.log("### Signing mode " + this.signingMethod);

                this.signingErrorState = [];
                this.signingMethod = signData.authorizationType;

                if (this.signingMethod === "TAC"
                    || this.signingMethod === "CARDTAN"
                    || this.signingMethod === "SID"
                    || this.signingMethod === "GEORGE_TOKEN"
                ) {
                    this.signingState = "WAIT_FOR_AUTHORIZATION";
                } else {
                    this.signingState = "ERROR";
                }

                if (this.signingMethod === "SID") {
                    this.startSidPolling(signData);
                }

                if (this.signingMethod === "GEORGE_TOKEN") {
                    this.startGeorgeTokenPolling(signData);
                }

                return signData;
            } catch (e) {
                this.isLoading = false;
                this.checkError(e);
                throw e;
            }
        } else {
            throw new Error("sign info missing");
        }
    }

    pollSign = async (url: string) => {
        const target = SigningAPI.pollSid(url);

        try {
            const data = await GenericAPIClient.requestType(target);
            debug.log("### polling data", data);

            const state = data.state ?? data.status;

            if (state === "OPEN"
                || state === "PROCESSING"
            ) {
                if (this.signingState === "WAIT_FOR_AUTHORIZATION") {
                    this.pollingTimer = setTimeout(() => this.pollSign(url), data.pollInterval ?? 3000) as any;
                }
            } else if (state === "DONE"
                || state === "NONE"
                || state === "AUTHORIZED"
            ) {
                try {
                    // only request PUT on SID, not GEORGE_TOKEN
                    let signingResponse;
                    if (this.signingMethod === "SID") {
                        signingResponse = await this.putSign();
                    } else if (this.signingMethod === "GEORGE_TOKEN") {
                        // when state is DONE the data object should look like ISigningResponse
                        // except with an addtional "state" key
                        signingResponse = data;
                    }
                    if (this.pollingOnSuccess) {
                        this.pollingOnSuccess(signingResponse);
                    }
                } catch (error) {
                    debug.log("### sid/george token signing failed");
                    this.checkError(error);
                    if (this.pollingOnError) {
                        this.pollingOnError(error);
                    }
                }
            } else if (state === "EXPIRED") {
                const errorState = this.signingMethod === "SID" ? "SID_EXPIRED" : "GEORGE_TOKEN_EXPIRED";
                this.signingErrorState.push(errorState);
                this.signingState = "ERROR";
            } else if (state === "REJECTED") {
                const errorState = this.signingMethod === "SID" ? "SID_REJECTED" : "GEORGE_TOKEN_REJECTED";
                this.signingErrorState.push(errorState);
                this.signingState = "ERROR";
            }
        } catch (e) {
            debug.log("### sid/george token polling failed");
            this.checkError(e);
            if (this.pollingOnError) {
                this.pollingOnError(e);
            }
        }
    }

    /**
     * In case of SID signing, prepare the URL so it works both for local proxy testing
     * as well as fat/production environments. Start polling.
     * @param signData 
     */
    async startSidPolling(signData: ISignData) {
        debug.log("### start polling");
        if (this.signingMethod !== "SID") {
            return;
        }

        if (!signData.poll
            || !signData.poll.url
        ) {
            throw new Error("SID signing without polling info");
        }

        this.pollingData = signData.poll;
        this.restartSidPolling();

        if (!externalInterfaceStore.isGeorgeWeb) {
            // HYB-123 open sID app
            safeWindowOpen(`georgeapp://at/softwaretoken_app?signId=${this.pollingData.id}&state=signing`);
        }
    }

    /**
     * In case of SID signing, prepare the URL so it works both for local proxy testing
     * as well as fat/production environments. Start polling.
     * @param signData 
     */
    async startGeorgeTokenPolling(signData: ISignData) {
        debug.log("### start polling");
        if (this.signingMethod !== "GEORGE_TOKEN") {
            return;
        }

        if (!signData.poll
            || !signData.poll.url
        ) {
            throw new Error("GEORGE_TOKEN signing without polling info");
        }

        this.pollingData = signData.poll;
        this.restartGeorgeTokenPolling();

        if (!externalInterfaceStore.isGeorgeWeb) {
            // HYB-123 open sID app
            safeWindowOpen(`georgeapp://at/george_token?authTransactionId=${signData.authTransactionId ?? ""}&signId=${signData.signInfo.signId ?? ""}&state=signing`);
        }
    }

    restartSidPolling() {
        if (this.signingMethod !== "SID") {
            return;
        }

        if (!this.pollingData) {
            return;
        }

        if (this.signingState !== "WAIT_FOR_AUTHORIZATION") {
            return;
        }

        // For local testing we replace the https:// prefix with our proxy url
        let url = this.pollingData.url.replace("https://", "");
        const interval = this.pollingData.interval || 3000;

        this.pollingTimer = setTimeout(() => this.pollSign(url), interval) as any;
    }

    restartGeorgeTokenPolling() {
        if (this.signingMethod !== "GEORGE_TOKEN") {
            return;
        }

        if (!this.pollingData) {
            return;
        }

        if (this.signingState !== "WAIT_FOR_AUTHORIZATION") {
            return;
        }

        // For local testing we replace the https:// prefix with our proxy url
        let url = this.pollingData.url.replace("https://", "");
        const interval = this.pollingData.interval || 3000;

        this.pollingTimer = setTimeout(() => this.pollSign(url), interval) as any;
    }

    clearPollingTimer() {
        if ((this.signingMethod === "SID" || this.signingMethod === "GEORGE_TOKEN") && this.pollingTimer) {
            clearTimeout(this.pollingTimer);
            this.pollingTimer = undefined;
        }
    }

    stopPolling() {
        this.signingState = "DONE";
        this.clearPollingTimer();
    }

    /**
     * Ends the signing procedure, for TAC and cardTAN this call sends the code, for SID no code is sent
     * @param oneTimePassword
     */
    @action async putSign(oneTimePassword?: string) {
        if (this.signingInfo
            && this.signingMethod
        ) {
            debug.log("### sending TAC or cardTAN");

            const target = SigningAPI.putSign(this.signingMethod, this.signingInfo, oneTimePassword);
            this.isLoading = true;

            try {
                const signData = await NetbankingAPIClient.requestType(target);
                this.isLoading = false;
                this.signingErrorState = [];
                this.signingState = "DONE";

                debug.log("### signing success", signData);

                return signData;
            } catch (e) {
                this.checkError(e);
                this.isLoading = false;
                throw e;
            }
        } else {
            throw new Error("sign info or phone missing");
        }
    }

    errorToStringId(e: ErrorCode): i18n.IDS {
        switch (e) {
            case "TAC_EXPIRED":
                return "signing.error.tacExpired";
            case "OTP_EXPIRED":
                return "signing.error.otpExpired";
            case "ONE_ATTEMPT_LEFT":
                return "signing.error.oneAttemptLeft";
            case "TAC_LIMIT_EXCEEDED":
                return "signing.error.limitExceeded";

            case "OTP_INVALID":
            case "TAC_INVALID":
            case "CARDTAN_INVALID":
            case "FIELD_TOO_SHORT":
            case "FIELD_TOO_LONG":
            case "FIELD_INVALID":
                return "signing.error.invalidCode";

            case "USER_LOCKED":
                return "signing.error.userLocked";
            case "SID_EXPIRED":
                return "sid.error.expired";
            case "SID_REJECTED":
                return "sid.error.rejected";
            case "GEORGE_TOKEN_EXPIRED":
                return "georgeIdentity.error.expired";
            case "GEORGE_TOKEN_REJECTED":
                return "georgeIdentity.error.rejected";
            case "AMOUNT_EXCEED_LIMITS":
                return "error.accountExceedLimits";

            default:
                return "signing.error.unknown";
        }
    }

    private checkError(error: any) {
        debug.log("### signing error", error);
        if (error instanceof APIClientStatusCodeError) {
            const errors: Array<IApiError> = error.response.errors;
            debug.log("### contained errors", errors);
            if (errors) {
                this.signingErrorState = _.map(errors, (e: IApiError): ErrorCode => {
                    return e.error as ErrorCode;
                });
                this.signingState = "ERROR";
            }
        }
        return error;
    }

    georgeAppDidBecomeActive() {
        // iOS stops the polling when George is in background -> we need to restart the polling manually
        this.clearPollingTimer();
        if (this.signingMethod === "SID") {
            this.restartSidPolling();
        } else if (this.signingMethod === "GEORGE_TOKEN") {
            this.restartGeorgeTokenPolling();
        }
    }

    georgeAppDidEnterBackground() {
        this.clearPollingTimer();
    }
}

export function getCurrentSigningStore(): SigningStore {
    return currentSigningStore;
}




// WEBPACK FOOTER //
// ./src/components/signing/stores/SigningStore.ts